import { DateTime } from "luxon";

/**
 * Timezone utility functions for handling appointment times across different timezones.
 *
 * FIXED: getOriginalTimeDisplay now handles both 12-hour and 24-hour time formats
 * based on user preferences, with fallback parsing for different input formats.
 */

/**
 * Gets the system's timezone
 * @returns {string} The system timezone (e.g., 'America/Toronto')
 */
export const getSystemTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Checks if an appointment's timezone differs from the system timezone
 * @param {string} appointmentTimezone - The appointment's timezone
 * @returns {boolean} True if the timezone differs from system timezone
 */
export const shouldShowOriginalTime = (appointmentTimezone) => {
  const systemTimezone = getSystemTimezone();
  return appointmentTimezone && appointmentTimezone !== systemTimezone;
};

/**
 * Gets the formatted time in the original timezone
 * @param {string} time - The time string to format
 * @param {string} appointmentTimezone - The appointment's timezone
 * @param {boolean} is12HourClock - Whether to use 12-hour format
 * @returns {string|null} The formatted time with timezone name, or null if invalid
 */
export const getOriginalTimeDisplay = (
  time,
  appointmentTimezone,
  is12HourClock = true
) => {
  if (!shouldShowOriginalTime(appointmentTimezone) || !time) {
    return null;
  }

  try {
    const systemTimezone = getSystemTimezone();

    // Try both 12-hour and 24-hour formats
    const format12Hour = "MMMM d, yyyy, h:mm a";
    const format24Hour = "MMMM d, yyyy, HH:mm";

    let originalTime;

    // First try parsing as ISO string (for raw API data)
    originalTime = DateTime.fromISO(time, {
      zone: systemTimezone,
    });

    // If ISO parsing fails, try the expected format based on user preference
    if (!originalTime.isValid) {
      const expectedFormat = is12HourClock ? format12Hour : format24Hour;
      originalTime = DateTime.fromFormat(time, expectedFormat, {
        zone: systemTimezone,
      });
    }

    // If that fails, try the other format
    if (!originalTime.isValid) {
      const fallbackFormat = is12HourClock ? format24Hour : format12Hour;
      originalTime = DateTime.fromFormat(time, fallbackFormat, {
        zone: systemTimezone,
      });
    }

    if (!originalTime.isValid) {
      return null;
    }

    const timeInOriginalTimezone = originalTime.setZone(appointmentTimezone);

    const timeZoneName = new Intl.DateTimeFormat("en-US", {
      timeZone: appointmentTimezone,
      timeZoneName: "long",
    })
      .formatToParts(timeInOriginalTimezone.toJSDate())
      .find((part) => part.type === "timeZoneName")?.value;

    // Use the user's preferred format for output
    const outputFormat = is12HourClock ? format12Hour : format24Hour;
    return timeInOriginalTimezone.toFormat(outputFormat) + " " + timeZoneName;
  } catch (error) {
    console.error("Error formatting time:", error);
    return null;
  }
};

/**
 * Gets the display name for a timezone (e.g., "Eastern Standard Time" or "Eastern Daylight Time")
 * @param {string} timezoneId - The timezone ID (e.g., "America/Toronto")
 * @param {Date} date - The date to use for DST context (defaults to current date)
 * @returns {string|null} The timezone display name or null if invalid
 */
export const getTimezoneName = (timezoneId, date = new Date()) => {
  if (!timezoneId) return null;

  try {
    const timeZoneName = new Intl.DateTimeFormat("en-US", {
      timeZone: timezoneId,
      timeZoneName: "long",
    })
      .formatToParts(date)
      .find((part) => part.type === "timeZoneName")?.value;

    return timeZoneName || null;
  } catch (error) {
    console.error("Error getting timezone name:", error);
    return null;
  }
};

/**
 * Converts a date to show the same wall-clock time in the local timezone
 * @param {Date} date - The date to convert
 * @param {string} sourceTimezone - The source timezone of the date
 * @returns {Date} The converted date in local timezone
 */
export const convertToLocalWallClockTime = (date, sourceTimezone) => {
  if (!date || !sourceTimezone) return date;

  try {
    // Get the wall-clock time from the source timezone
    const sourceTime = DateTime.fromISO(date.toISOString(), {
      zone: "utc",
    }).setZone(sourceTimezone);

    // Create a new datetime in local timezone with the same wall-clock time
    const localTime = DateTime.fromObject(
      {
        year: sourceTime.year,
        month: sourceTime.month,
        day: sourceTime.day,
        hour: sourceTime.hour,
        minute: sourceTime.minute,
        second: sourceTime.second,
        millisecond: sourceTime.millisecond,
      },
      { zone: getSystemTimezone() }
    );

    return localTime.toJSDate();
  } catch (error) {
    console.error("Error converting time:", error);
    return date;
  }
};

/**
 * Creates a Date object from an ISO date string that represents the correct calendar day
 * in the specified timezone, avoiding UTC midnight conversion issues
 * @param {string} isoDateString - The ISO date string (e.g., "2025-07-28")
 * @param {string} timezoneId - The timezone ID (e.g., "America/Toronto")
 * @returns {Date} A Date object representing the correct calendar day
 */
export const createDateInTimezone = (isoDateString, timezoneId) => {
  if (!isoDateString || !timezoneId) {
    return new Date(isoDateString);
  }

  try {
    // Create a DateTime object representing midnight in the target timezone
    const dateInTimezone = DateTime.fromISO(`${isoDateString}T00:00:00`, {
      zone: timezoneId,
    });

    if (!dateInTimezone.isValid) {
      console.warn("Invalid date or timezone:", isoDateString, timezoneId);
      return new Date(isoDateString);
    }

    // Convert to local timezone but keep the same calendar day representation
    return dateInTimezone.toJSDate();
  } catch (error) {
    console.error("Error creating date in timezone:", error);
    return new Date(isoDateString);
  }
};
